import { LOB_IDS } from "@/shared/common/static";
import { LOCALISATION_CONFIG } from "@/shared/config/localisation";
import type { SharedPersonalDetailsState } from "@/shared/redux/reducers/personalDetailsSlice";
import { getAuthSessionIdentifiers } from "@/shared/utils/authSession";

import type {
  TravelAdditionalInfoValues,
  TravelDetailsFormValues,
  TravelPaymentValues,
  TravelPersonalFormValues,
  TravelUploadedDocumentMap,
} from "../redux/type";

type TravelQuoteTravellerPayload = {
  name: string;
  first_name: string;
  last_name: string;
  date_of_birth: string | null;
};

const DEFAULT_TRAVEL_OPTIONAL_COVERS = [
  { key: "ADVENTURE_SPORTS", value: "false" },
  { key: "TERRORISM", value: "false" },
  { key: "WINTER_SPORTS", value: "false" },
  { key: "GOLF", value: "false" },
  { key: "COVID", value: "false" },
];

export type TravelQuoteRequestBody = {
  personal_details: {
    first_name: string;
    last_name: string;
    mobile_country_code: string;
    mobile_number: string;
    email: string;
    partner_code: string;
    friends_and_family_contact: string;
    marketing_consent: "yes" | "no";
  };
  travel_details: {
    coverage_type: string;
    plan_type: "single" | "annual";
    travel_dates: {
      start_date: string | null;
      end_date: string | null;
    };
    cover_type: "Individual" | "Group" | "Family";
    travellers: TravelQuoteTravellerPayload[];
    departure: string;
    destination: string;
  };
  client_id: number | null;
  session_id: number | null;
  lob_id?: number;
};

function normalizeCoverType(
  coverType: TravelDetailsFormValues["coverType"]
): "Individual" | "Group" | "Family" {
  if (coverType === "group") {
    return "Group";
  }

  if (coverType === "family") {
    return "Family";
  }

  return "Individual";
}

function splitTravellerName(name: string) {
  const [firstName, ...restName] = name.trim().split(/\s+/).filter(Boolean);

  return {
    firstName: firstName || "",
    lastName: restName.join(" "),
  };
}

function normalizeDateOfBirth(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }

  const normalized = value.trim();
  if (!normalized) {
    return null;
  }

  return /^\d{4}-\d{2}-\d{2}$/.test(normalized) ? normalized : null;
}

function normalizeNullableNumber(value: string | number | null | undefined) {
  const normalizedValue = value === null || value === undefined ? null : Number(value);

  return normalizedValue === null || Number.isNaN(normalizedValue) ? null : normalizedValue;
}

function getPaymentResponsePayload(paymentResponse: any) {
  return paymentResponse?.data && typeof paymentResponse.data === "object"
    ? paymentResponse.data
    : paymentResponse;
}

function buildTravelPlanName(selectedQuote: any) {
  const insurerPrefix = selectedQuote?.insurer?.name
    ?.replace(/insurance|insurer|pjsc|limited|co/gi, "")
    .trim();

  return insurerPrefix
    ? `${insurerPrefix}-${selectedQuote?.plan?.name}`
    : selectedQuote?.plan?.name;
}

function buildTravelSelectedOptionalCovers(additionalInfo: any) {
  return additionalInfo?.optionalCovers || DEFAULT_TRAVEL_OPTIONAL_COVERS;
}

function buildTravelTravellersMoreInfo(
  travelDetails: TravelDetailsFormValues,
  additionalInfo: any
) {
  return (travelDetails?.travellers || []).map((traveller: any, index: number) => {
    const { firstName, lastName } = splitTravellerName(traveller.name || "");

    if (index === 0) {
      return {
        first_name: firstName,
        last_name: lastName,
        date_of_birth: normalizeDateOfBirth(traveller.dob),
        gender: additionalInfo?.gender || "Male",
        nationality: additionalInfo?.nationality || "",
        cityOfResidence: additionalInfo?.cityOfResidence || "",
        passportNo: additionalInfo?.passportNumber || additionalInfo?.passportNo || "",
        beneficiaryName: additionalInfo?.beneficiaryName || "",
        beneficiaryRelationship:
          additionalInfo?.beneficiaryRelation || additionalInfo?.beneficiaryRelationship || "",
        address: additionalInfo?.address || "",
        sourceOfFunds: additionalInfo?.sourceOfFund || additionalInfo?.sourceOfFunds || "",
        emiratesId: additionalInfo?.emiratesId || "",
        emiratesIdExpiryDate: additionalInfo?.emiratesIdExpiryDate || null,
        emiratesIdIssueDate: additionalInfo?.emiratesIdIssueDate || null,
        passportExpiryDate: additionalInfo?.passportExpiryDate || null,
        passportIssueDate: additionalInfo?.passportIssueDate || null,
      };
    }

    const extraTravellerInfo = additionalInfo?.travellers?.[index - 1] || {};

    return {
      first_name: firstName,
      last_name: lastName,
      date_of_birth: normalizeDateOfBirth(traveller.dob),
      gender: extraTravellerInfo?.gender || "Male",
      nationality: extraTravellerInfo?.nationality || "",
      passportNo: extraTravellerInfo?.passportNumber || extraTravellerInfo?.passportNo || "",
    };
  });
}

export function buildTravelPersonalDetailsPayload(personalDetails: SharedPersonalDetailsState) {
  return {
    first_name: personalDetails.firstName || "",
    last_name: personalDetails.lastName || "",
    mobile_country_code: `+${personalDetails.mobileNumber.countryCode.replace(/\D/g, "")}`,
    mobile_number: personalDetails.mobileNumber.number || "",
    email: personalDetails.email || "",
    partner_code: personalDetails.partnerCode || "",
    friends_and_family_contact: personalDetails.friendsAndFamilyContact || "",
    marketing_consent: personalDetails.marketingConsent || "no",
  } as const;
}

export function buildTravelDetailsPayload(
  travelDetails: TravelDetailsFormValues,
  personalDetails: SharedPersonalDetailsState
) {
  return {
    coverage_type: travelDetails.coverage,
    plan_type: travelDetails.planType,
    travel_dates: {
      start_date: travelDetails.startDate || null,
      end_date: travelDetails.endDate || null,
    },
    cover_type: normalizeCoverType(travelDetails.coverType),
    travellers: travelDetails.travellers.map((traveller) => {
      const { firstName, lastName } = splitTravellerName(traveller.name || "");

      return {
        name: traveller.name || "",
        first_name: firstName || personalDetails.firstName || "",
        last_name: lastName || personalDetails.lastName || "",
        date_of_birth: traveller.dob || null,
      };
    }),
    departure: travelDetails.fromCountry || "",
    destination: travelDetails.destination || "",
  } as const;
}

export function buildTravelQuoteRequestBody(
  personalDetails: SharedPersonalDetailsState,
  travelDetails: TravelDetailsFormValues,
  authSession: unknown
): TravelQuoteRequestBody {
  const { clientId, sessionId } = getAuthSessionIdentifiers(authSession);
  const normalizedClientId = clientId === null || clientId === undefined ? null : Number(clientId);
  const normalizedSessionId =
    sessionId === null || sessionId === undefined ? null : Number(sessionId);

  return {
    personal_details: buildTravelPersonalDetailsPayload(personalDetails),
    travel_details: buildTravelDetailsPayload(travelDetails, personalDetails),
    client_id: Number.isNaN(normalizedClientId) ? null : normalizedClientId,
    session_id: Number.isNaN(normalizedSessionId) ? null : normalizedSessionId,
    lob_id: LOB_IDS.TRAVEL,
  };
}

export function buildTravelCreateSessionPayload(personalData: TravelPersonalFormValues) {
  return {
    assigned_user_id: null,
    payload: {
      first_name: personalData.firstName,
      last_name: personalData.lastName || "",
      country_code: personalData.mobileNumber.countryCode.replace(/\D/g, ""),
      mobile_number: personalData.mobileNumber.number,
      email: personalData.email,
      partner_code: personalData.partnerCode || "",
      friends_and_family_contact: personalData.friendsAndFamilyContact || "",
      marketing_consent: personalData.marketingConsent || "no",
    },
    lob_id: LOB_IDS.TRAVEL,
  };
}

export function buildTravelSelectedQuotePayload(quote: any) {
  const commission = quote?.plan?.commission || quote?.commission;

  return {
    insurer_name: quote?.insurer?.name || "",
    insurer_id: quote?.insurer?.insurer_id || quote?.insurer?.id || null,
    insurer_logo: quote?.insurer?.insurer_logo || null,
    plan_name: quote?.plan?.name || "",
    plan_id: quote?.plan?.id || null,
    premium: quote?.plan?.price?.amount ?? null,
    vat: quote?.plan?.price?.vat ?? null,
    total_premium: quote?.plan?.price?.amount_with_vat ?? null,
    currency: quote?.plan?.price?.currency || null,
    sections: quote?.sections || [],
    selected_addOns:
      quote?.sections
        ?.find((section: any) => section?.title === "Add-Ons")
        ?.items?.filter((item: any) => item?.is_checked === true)
        ?.map((item: any) => item?.label) || [],
    ...(commission
      ? {
          commission: {
            percentage: commission?.percentage ?? null,
            amount: commission?.amount ?? null,
            currency: commission?.currency || null,
          },
        }
      : {}),
  };
}

export function buildTravelAdditionalPersonalDetailsPayload(
  additionalInfo: TravelAdditionalInfoValues
) {
  return {
    gender: additionalInfo.gender,
    beneficiaryName: additionalInfo.beneficiaryName,
    beneficiaryRelationship: additionalInfo.beneficiaryRelation,
    address: additionalInfo.address,
    nationality: additionalInfo.nationality,
    sourceOfFunds: additionalInfo.sourceOfFund,
    cityOfResidence: additionalInfo.cityOfResidence,
    travellers: additionalInfo.travellers?.map((traveller, index) => ({
      travellerNo: index + 2,
      gender: traveller.gender,
      nationality: traveller.nationality,
    })),
  };
}

export function buildTravelIdentityPayload(additionalInfo: TravelAdditionalInfoValues) {
  return {
    emiratesId: additionalInfo.emiratesId || "",
    emiratesIdExpiryDate: "",
    emiratesIdIssueDate: "",
    passportNo: additionalInfo.passportNumber || "",
    passportExpiryDate: "",
    passportIssueDate: "",
    travellers: additionalInfo.travellers?.map((traveller, index) => ({
      travellerNo: index + 2,
      passportNo: traveller.passportNumber || "",
    })),
  };
}

export function buildTravelAttachmentsPayload(documents: TravelUploadedDocumentMap) {
  return Object.entries(documents || {}).reduce<Record<string, unknown>>(
    (accumulator, [fieldKey, document]) => {
      if (!document) {
        return accumulator;
      }

      accumulator[fieldKey] = {
        docName: document.fileName || document.filename,
        docType: document.mimeType || "",
        doc_url:
          (document.uploadResponse as { payload?: { final_filename?: string } } | undefined)
            ?.payload?.final_filename || "",
        size: document.size || 0,
        uploadedAt: document.uploadedAt,
      };

      return accumulator;
    },
    {}
  );
}

function toTravelAttachType(fieldKey: string) {
  const parts = fieldKey.split("_");
  const isAdditionalTravellerDocument = parts[0] === "traveller" && parts.length > 2;
  const travellerIndex = isAdditionalTravellerDocument ? parts[1] : "";
  const documentKey = isAdditionalTravellerDocument ? parts.slice(2).join("_") : fieldKey;
  const formattedDocumentKey = documentKey
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join("");

  return isAdditionalTravellerDocument
    ? `${formattedDocumentKey}${travellerIndex}`
    : formattedDocumentKey;
}

function buildTravelUploadDocAttachmentsPayload(documents: TravelUploadedDocumentMap) {
  return Object.entries(documents || {}).reduce<Record<string, unknown>[]>(
    (attachments, [fieldKey, document]) => {
      if (!document) {
        return attachments;
      }

      attachments.push({
        docName: document.fileName || document.filename || "uploaded_document",
        docType: document.mimeType || "image/jpeg",
        file:
          (document as any).file || (document as any).base64 || (document as any).fileData || "",
        attachType: toTravelAttachType(fieldKey),
      });

      return attachments;
    },
    []
  );
}

export function buildTravelBindPayload(params: {
  selectedQuote: any;
  clientId: string | number | null;
  sessionId: string | number | null;
  travelDetails: TravelDetailsFormValues;
  additionalInfo: any;
}) {
  const { selectedQuote, clientId, sessionId, travelDetails, additionalInfo } = params;

  return {
    client_id: normalizeNullableNumber(clientId),
    session_id: normalizeNullableNumber(sessionId),
    lob_id: LOB_IDS.TRAVEL,
    plan_name: buildTravelPlanName(selectedQuote),
    insurerId: String(selectedQuote?.insurer?.insurer_id ?? ""),
    action: "NEXT_BUTTON",
    insurerPlan: {
      id: String(selectedQuote?.plan?.id ?? ""),
      planName: selectedQuote?.plan?.name || "",
    },
    selectedOptionalCovers: buildTravelSelectedOptionalCovers(additionalInfo),
    travellers_more_info: buildTravelTravellersMoreInfo(travelDetails, additionalInfo),
    declarations: {
      isUaeResidentConfirmed: additionalInfo?.uaeResidentConfirm ?? true,
    },
    attachments: [],
  };
}

export function buildTravelBuyNowBindPayload(params: {
  selectedQuote: any;
  clientId: string | number | null;
  sessionId: string | number | null;
  travelDetails: TravelDetailsFormValues;
  additionalInfo: any;
}) {
  const payload = buildTravelBindPayload(params);

  return {
    ...payload,
    action: "BUY",
  };
}

export function buildTravelPaymentPayload(params: {
  selectedQuote: any;
  clientId: string | number | null;
  sessionId: string | number | null;
  paymentData: TravelPaymentValues;
}) {
  const { selectedQuote, clientId, sessionId, paymentData } = params;
  const amountWithVat =
    Number(selectedQuote?.plan?.price?.amount_with_vat) ||
    Number(selectedQuote?.plan?.price?.amount) ||
    0;

  return {
    client_id: clientId ? Number(clientId) : null,
    quote_id: sessionId ? Number(sessionId) : 0,
    currency: String(
      selectedQuote?.plan?.price?.currency || LOCALISATION_CONFIG.currencyCode
    ).toLowerCase(),
    payment_method: "card",
    amount: amountWithVat,
    plan_name: selectedQuote?.plan?.name || "",
    provider:
      selectedQuote?.insurer?.name && selectedQuote?.plan?.name
        ? `${selectedQuote.insurer.name} - ${selectedQuote.plan.name}`
        : selectedQuote?.insurer?.name || "Unknown",
    card_number: paymentData.cardNumber.replace(/\s/g, ""),
    card_expiry: paymentData.expiry,
    card_cvv: paymentData.cvv,
    card_name: paymentData.cardHolder,
  };
}

export function buildTravelIssuePolicyPayload(params: {
  selectedQuote: any;
  clientId: string | number | null;
  sessionId: string | number | null;
  paymentResponse: any;
  travelDetails: Pick<TravelDetailsFormValues, "startDate" | "endDate">;
  lobId?: number;
}) {
  const { selectedQuote, clientId, sessionId, paymentResponse, travelDetails, lobId } = params;
  const paymentPayload = getPaymentResponsePayload(paymentResponse);

  return {
    insurerId: Number(selectedQuote?.insurer?.insurer_id ?? 0) || null,
    action: "ISSUE_POLICY",
    lob_id: lobId ?? LOB_IDS.TRAVEL,
    client_id: clientId ? Number(clientId) : null,
    session_id: sessionId ? Number(sessionId) : null,
    policy_issued_by: "retail",
    plan_name: paymentPayload?.plan_name || selectedQuote?.plan?.name || "",
    start_date: travelDetails.startDate || null,
    end_date: travelDetails.endDate || null,
    provider_transaction_id:
      paymentPayload?.provider_transaction_id || paymentPayload?.transaction_id || "",
  };
}

export function buildTravelUploadDocPayload(params: {
  selectedQuote: any;
  clientId: string | number | null;
  sessionId: string | number | null;
  travelDetails: TravelDetailsFormValues;
  additionalInfo: TravelAdditionalInfoValues;
  documents: TravelUploadedDocumentMap;
  lob_id?: number;
}) {
  const { selectedQuote, clientId, sessionId, travelDetails, additionalInfo, documents, lob_id } =
    params;

  return {
    client_id: normalizeNullableNumber(clientId),
    session_id: normalizeNullableNumber(sessionId),
    lob_id: lob_id ?? LOB_IDS.TRAVEL,
    plan_name: buildTravelPlanName(selectedQuote),
    insurerId: String(selectedQuote?.insurer?.insurer_id ?? ""),
    action: "UPLOAD_DOC",
    insurerPlan: {
      id: String(selectedQuote?.plan?.id ?? ""),
      planName: selectedQuote?.plan?.name || "",
    },
    selectedOptionalCovers: buildTravelSelectedOptionalCovers(additionalInfo),
    travellers_more_info: buildTravelTravellersMoreInfo(travelDetails, additionalInfo),
    declarations: {
      isUaeResidentConfirmed: additionalInfo?.uaeResidentConfirm ?? true,
    },
    attachments: buildTravelUploadDocAttachmentsPayload(documents),
  };
}
